import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function Notifications() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchNotifications = async () => {
      try {
        const user = JSON.parse(sessionStorage.getItem("user"));

        const collegeId = sessionStorage.getItem("selected_college_id") || user?.college_id;

        const roleId = sessionStorage.getItem("selected_role_id") || user?.role_id;

        if (!user?.client_id || !collegeId || !user?.user_id || !roleId) {
          if (!cancelled) setNotifications([]);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/notifications/${user.client_id}/${collegeId}/${user.user_id}/${roleId}?limit=100&offset=0`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const result = await response.json();

        if (!cancelled) {
          setNotifications(result.success && Array.isArray(result.data) ? result.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching notifications:", error);
          setNotifications([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotifications();

    return () => {
      cancelled = true;
    };
  }, [API_BASE_URL]);

  const unreadCount = notifications.filter((item) => Number(item.is_read) !== 1).length;

  const handleNotificationClick = (item) => {
    if (!item.route_path) return;

    const route = String(item.route_path).trim();

    // Prevent external or malformed navigation.
    if (!route.startsWith("/") || route.startsWith("//") || route.includes("\\")) {
      console.error("Invalid notification route:", route);
      return;
    }

    const token = sessionStorage.getItem("token");

    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    // Open the corresponding registered React Router page.
    navigate(route);
  };

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="35vh"
          gap={2}
        >
          <CircularProgress color="info" />

          <MDTypography variant="button" color="text">
            Loading notifications...
          </MDTypography>
        </MDBox>
      );
    }

    if (notifications.length === 0) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py={6}
        >
          <Avatar
            sx={{
              width: 75,
              height: 75,
              mb: 2,
              bgcolor: "#E3F2FD",
              color: "#1976D2",
            }}
          >
            <NotificationsActiveIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <MDTypography variant="h6" fontWeight="medium">
            No Notifications Available
          </MDTypography>

          <MDTypography variant="button" color="text">
            New notifications will appear here.
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox p={{ xs: 1, sm: 2 }}>
        {/* Notification summary */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <MDTypography variant="h6" fontWeight="medium">
            RECENT NOTIFICATIONS
          </MDTypography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<NotificationsActiveIcon />}
              label={`${notifications.length} Total`}
              size="small"
              sx={{
                bgcolor: "#E3F2FD",
                color: "#1565C0",
                fontWeight: 700,
                "& .MuiChip-icon": { color: "#1565C0" },
              }}
            />

            <Chip
              icon={<MarkEmailReadIcon />}
              label={`${unreadCount} Unread`}
              size="small"
              sx={{
                bgcolor: "#FFF3E0",
                color: "#E65100",
                fontWeight: 700,
                "& .MuiChip-icon": { color: "#E65100" },
              }}
            />
          </Stack>
        </MDBox>

        <Divider sx={{ mb: 2 }} />

        {/* Notifications inside the main card frame */}
        <Stack spacing={1.5}>
          {notifications.map((item) => {
            const isUnread = Number(item.is_read) !== 1;
            const hasRoute = Boolean(item.route_path);

            return (
              <MDBox
                key={String(item.notification_id)}
                role={hasRoute ? "link" : undefined}
                tabIndex={hasRoute ? 0 : undefined}
                onClick={() => handleNotificationClick(item)}
                onKeyDown={(event) => {
                  if (hasRoute && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    handleNotificationClick(item);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: isUnread ? "#90CAF9" : "#E0E0E0",
                  backgroundColor: isUnread ? "#F4F9FF" : "#FFFFFF",
                  cursor: hasRoute ? "pointer" : "default",
                  transition: "all 0.2s ease",
                  "&:hover": hasRoute
                    ? {
                        backgroundColor: "#EAF4FF",
                        borderColor: "#2196F3",
                        transform: "translateY(-2px)",
                        boxShadow: "0 5px 14px rgba(33,150,243,0.12)",
                      }
                    : {},
                  "&:focus-visible": {
                    outline: "2px solid #1976D2",
                    outlineOffset: 2,
                  },
                }}
              >
                {/* Notification icon */}
                <Avatar
                  sx={{
                    width: 45,
                    height: 45,
                    flexShrink: 0,
                    bgcolor: isUnread ? "#1976D2" : "#ECEFF1",
                    color: isUnread ? "#FFFFFF" : "#607D8B",
                  }}
                >
                  <NotificationsActiveIcon />
                </Avatar>

                {/* Notification details */}
                <MDBox flex={1} minWidth={0}>
                  <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={0.7}>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color="dark"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.title || "Notification"}
                    </MDTypography>

                    {isUnread && <FiberManualRecordIcon sx={{ fontSize: 10, color: "#2196F3" }} />}

                    {isUnread && (
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "9px",
                          fontWeight: 800,
                          bgcolor: "#E3F2FD",
                          color: "#1565C0",
                        }}
                      />
                    )}
                  </MDBox>

                  {item.message && (
                    <MDTypography
                      variant="button"
                      color="text"
                      display="block"
                      sx={{
                        mt: 0.7,
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item.message}
                    </MDTypography>
                  )}

                  <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={1.2}>
                    <Chip
                      label={item.module_name || "Notification"}
                      size="small"
                      sx={{
                        height: 23,
                        fontSize: "10px",
                        fontWeight: 700,
                        bgcolor: "#E8EAF6",
                        color: "#3949AB",
                        maxWidth: "100%",
                      }}
                    />

                    {item.created_at && (
                      <MDTypography variant="caption" color="text">
                        <Icon
                          sx={{
                            fontSize: 13,
                            verticalAlign: "middle",
                            mr: 0.4,
                          }}
                        >
                          schedule
                        </Icon>
                        {formatDate(item.created_at)}
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>

                {/* Open corresponding page */}
                {hasRoute && (
                  <Tooltip title="Open notification">
                    <IconButton
                      size="small"
                      aria-label="Open notification"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleNotificationClick(item);
                      }}
                      sx={{
                        mt: 0.5,
                        color: "#1976D2",
                        flexShrink: 0,
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </MDBox>
            );
          })}
        </Stack>
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={2} pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              {/* HEADER - Same frame style as Library */}
              <MDBox
                mx={2}
                mt={-1}
                py={1}
                px={2}
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "#FFFFFF" }}>notifications_active</Icon>

                  <MDTypography variant="h6" color="white">
                    NOTIFICATIONS
                  </MDTypography>
                </MDBox>

                <MDButton
                  variant="contained"
                  onClick={() => navigate("/dashboard")}
                  startIcon={<Icon>arrow_back</Icon>}
                  sx={{
                    mb: 0,
                    color: "#FFFFFF",
                    backgroundColor: "rgba(255,255,255,0.18)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.28)",
                    },
                  }}
                >
                  Dashboard
                </MDButton>
              </MDBox>

              {/* CONTENT - Notifications stay inside this frame */}
              <MDBox p={1}>{renderContent()}</MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox display="flex" flexDirection="column" minHeight="30vh" />

      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
